import { useState, useEffect } from 'react';
import {
  collectionGroup, query, where, getDocs, doc, getDoc, onSnapshot,
  setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useResolvedBoard — resolves a boardId to its concrete board doc,
 * even when the user is a guest editor/viewer on someone else's board.
 *
 * The data model lives at users/{ownerUid}/boards/{boardId}/... — so a
 * user navigating to /board/abc123 from a share link doesn't find the
 * board in their own subtree. We have to fall back to a collectionGroup
 * lookup that searches across all `boards` collections for one where
 * the boardId matches AND the current user is in the editors/viewers
 * array.
 *
 * Returns:
 *   { board, ownerUid, loading, error }
 *
 *   - On the happy path (own board): board is from `ownBoards`, ownerUid = currentUid
 *   - On a shared board: board is the resolved doc, ownerUid is the actual owner
 *   - On miss / no access: { board: null, ownerUid: null, loading: false }
 *
 * Once resolved, we also start a real-time listener on the resolved doc
 * so updates (rename, ACL changes) flow through. For own boards the
 * upstream useBoards already handles this.
 *
 * Indexes required (firestore.indexes.json):
 *   collectionGroup boards, fields [editors, __name__]
 *   collectionGroup boards, fields [viewers, __name__]
 *   These make the array-contains queries efficient. Without them, the
 *   first run logs a console error with a magic-link-to-create button.
 */
export function useResolvedBoard({ boardId, currentUid, ownBoards }) {
  const [state, setState] = useState({
    board: null,
    ownerUid: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!boardId || !currentUid) {
      setState({ board: null, ownerUid: null, loading: false, error: null });
      return undefined;
    }

    // Fast path: it's one of the user's own boards.
    const own = (ownBoards || []).find(b => b.id === boardId);
    if (own) {
      setState({ board: own, ownerUid: currentUid, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    let unsubLive = null;

    // Live listener on the user's OWN board path — used by the cache-first
    // fast path B below. (For boards already in `ownBoards`, the upstream
    // useBoards listener covers updates, so fast path A skips this.)
    const startOwnListener = () => {
      unsubLive = onSnapshot(
        doc(db, 'users', currentUid, 'boards', boardId),
        (snap) => {
          if (cancelled || !snap.exists()) return; // don't clobber on a transient cache miss
          setState({ board: { id: snap.id, ...snap.data() }, ownerUid: currentUid, loading: false, error: null });
        },
        (err) => { console.warn('[useResolvedBoard] own live snapshot error', err); },
      );
    };

    async function resolve() {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Fast path B (Walter 2026-06-24, offline hardening): before the
      // cross-user lookup, try a DIRECT read of the board at the user's own
      // path. getDoc serves from the local cache when offline, so an own board
      // resolves even on a cold offline boot — before `ownBoards` has loaded
      // from cache and without the collectionGroup queries (which need the
      // network or a warm query cache). If the doc isn't there, fall through to
      // the shared-board lookup.
      try {
        const ownSnap = await getDoc(doc(db, 'users', currentUid, 'boards', boardId));
        if (cancelled) return;
        if (ownSnap.exists()) {
          setState({ board: { id: ownSnap.id, ...ownSnap.data() }, ownerUid: currentUid, loading: false, error: null });
          startOwnListener();
          return;
        }
      } catch (err) {
        // Non-fatal — fall through to the cross-user lookup below.
        if (cancelled) return;
      }

      // Slow path: cross-user lookup via collectionGroup. We run two
      // queries in parallel (editors + viewers) and merge.
      try {
        const editorsQ = query(
          collectionGroup(db, 'boards'),
          where('editors', 'array-contains', currentUid),
        );
        const viewersQ = query(
          collectionGroup(db, 'boards'),
          where('viewers', 'array-contains', currentUid),
        );
        const [editorsSnap, viewersSnap] = await Promise.all([
          getDocs(editorsQ),
          getDocs(viewersQ),
        ]);
        if (cancelled) return;

        // Find the doc whose ID matches the boardId we want.
        let match = null;
        for (const snap of [editorsSnap, viewersSnap]) {
          for (const d of snap.docs) {
            if (d.id === boardId) {
              match = d;
              break;
            }
          }
          if (match) break;
        }

        if (!match) {
          // Walter 2026-06-24: offline, an uncached shared board legitimately
          // can't be found in the local cache — that's NOT a real "no access".
          // Flag it as 'offline' so we don't show the alarming trouble/extension
          // copy; LoadStuckScreen reads navigator.onLine and shows the calm
          // "waiting to reconnect" state, then auto-recovers when back online.
          const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
          setState({ board: null, ownerUid: null, loading: false, error: offline ? 'offline' : 'no-access' });
          return;
        }

        // Owner uid lives in the path: users/{ownerUid}/boards/{boardId}
        const ownerUid = match.ref.parent.parent.id;
        const data = { id: match.id, ...match.data() };

        setState({ board: data, ownerUid, loading: false, error: null });

        // "Shared with me" pointer — write a small doc into the
        // current user's own subtree pointing at the resolved board.
        // The Sidebar's shared-with-me section reads this collection
        // to render shared boards in the user's nav. We do this lazily
        // (on first successful resolve) instead of via a Cloud Function
        // fan-out, which avoids the cross-user write entirely.
        //
        // setDoc with merge means re-resolving an already-saved
        // pointer doesn't churn — only fields that changed get written.
        try {
          await setDoc(
            doc(db, 'users', currentUid, 'sharedWithMe', boardId),
            {
              kind: 'board',
              refOwnerUid: ownerUid,
              refId: boardId,
              role: data.editors?.includes(currentUid) ? 'editor' : 'viewer',
              cachedTitle: data.name || '(untitled)',
              addedAt: serverTimestamp(),
            },
            { merge: true },
          );
        } catch (err) {
          // Non-fatal — sidebar entry just won't appear.
          console.warn('[useResolvedBoard] sharedWithMe pointer write failed', err);
        }

        // Switch to a live listener on the resolved doc so renames,
        // ACL changes, mode flips etc. flow through. Returns an
        // unsubscribe we keep around for cleanup below.
        unsubLive = onSnapshot(
          doc(db, 'users', ownerUid, 'boards', boardId),
          (snap) => {
            if (cancelled) return;
            if (!snap.exists()) {
              setState({ board: null, ownerUid: null, loading: false, error: 'deleted' });
              return;
            }
            setState(prev => ({
              ...prev,
              board: { id: snap.id, ...snap.data() },
              ownerUid,
              loading: false,
              error: null,
            }));
          },
          (err) => {
            console.warn('[useResolvedBoard] live snapshot error', err);
            // Most likely cause: rules denied — owner removed us from
            // the ACL since we resolved. Treat as no-access.
            setState({ board: null, ownerUid: null, loading: false, error: 'no-access' });
          }
        );
      } catch (err) {
        if (cancelled) return;
        console.error('[useResolvedBoard] resolve failed', err);
        setState({ board: null, ownerUid: null, loading: false, error: err.message });
      }
    }

    resolve();
    return () => {
      cancelled = true;
      if (unsubLive) unsubLive();
    };
  }, [boardId, currentUid, ownBoards]);

  return state;
}
